import { colors } from 'pixel-base'
import { baseChartStyles } from './chartStyles'

export interface TopProduct {
  base_product_id: number | string
  product_name: string
  total_items_sold: number
  total_revenue: number
  items_returned: number
  revenue_returned: number
  collection_names: string[]
  variations: {
    variation_id: number | string
    sku: string
    product_name: string
    items_sold: number
    revenue: number
    items_returned: number
    revenue_returned: number
    image: string | null
  }[]
}

export interface ChartFilters {
  start_date: Date
  end_date: Date
  data_type: string
}

// Shared chart options configuration
export const createChartOptions = (sortedData: TopProduct[]) => ({
  ...baseChartStyles,
  legend: {
    orient: 'horizontal',
    top: 25,
    left: 'center',
    padding: [0, 10, 10, 10],
    textStyle: {
      fontSize: 12,
      color: '#333',
    },
  },
  grid: {
    ...baseChartStyles.grid,
  },
  tooltip: {
    ...baseChartStyles.tooltip,
    formatter: (params: any) => {
      const param = params[0]
      const product = param.name
      const productData = sortedData.find(
        (item) => item.product_name === product,
      )

      if (!productData) return ''

      const imagePath =
        productData?.variations && productData.variations.length > 0
          ? productData.variations[0].image || ''
          : ''
      const revenue = productData?.total_revenue || 0
      const itemsSold = productData?.total_items_sold || 0
      const itemsReturned = productData?.items_returned || 0
      const revenueReturned = productData?.revenue_returned || 0
      const collections = productData?.collection_names || []
      const variations = productData?.variations || []

      // Calculate average price and return rate
      const avgPrice = itemsSold > 0 ? revenue / itemsSold : 0
      const totalItems = itemsSold + itemsReturned
      const returnRate = totalItems > 0 ? (itemsReturned / totalItems) * 100 : 0
      const netRevenue = revenue - revenueReturned
      const netItems = itemsSold - itemsReturned

      return `
        <div style="display: flex; gap: 16px; align-items: start; max-width: 550px; padding: 12px; background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%); border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1);">
          <div style="display: flex; flex-direction: column; gap: 8px; flex-shrink: 0;">
            ${
              imagePath
                ? `
              <div style="width: 80px; height: 80px; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <img src="${imagePath}" style="width: 100%; height: 100%; object-fit: cover;" />
              </div>
            `
                : `
              <div style="width: 80px; height: 80px; background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%); display: flex; align-items: center; justify-content: center; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <span style="color: #6c757d; font-size: 12px; font-weight: 500;">No Image</span>
              </div>
            `
            }
            <!-- Collections under image -->
            ${
              collections.length > 0
                ? `
            <div style="width: 80px;">
              <div style="font-size: 10px; color: #6c757d; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Collections</div>
              <div style="display: flex; flex-direction: column; gap: 3px;">
                ${collections
                  .map(
                    (collection) => `
                  <span style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 2px 6px; border-radius: 12px; font-size: 9px; font-weight: 500; text-align: center;">${collection}</span>
                `,
                  )
                  .join('')}
              </div>
            </div>
          `
                : ''
            }
          </div>
          
          <div style="flex: 1; min-width: 0;">
            <div style="font-size: 17px; font-weight: 700; margin-bottom: 10px; color: #2c3e50; line-height: 1.3;">${product}</div>
            
            <!-- Key Metrics at the top -->
            <div style="display: grid; grid-template-columns: ${
              itemsReturned > 0 ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr'
            }; gap: 8px; margin-bottom: 16px; padding: 12px; background: rgba(255,255,255,0.8); border-radius: 6px; border: 1px solid rgba(0,0,0,0.05);">
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #5f38f9; margin-bottom: 3px;">PKR ${revenue.toLocaleString()}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Revenue</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #28a745; margin-bottom: 3px;">${itemsSold.toLocaleString()}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Items Sold</div>
              </div>
              ${
                itemsReturned > 0
                  ? `
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #dc3545; margin-bottom: 3px;">${itemsReturned.toLocaleString()}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Returned</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #f39c12; margin-bottom: 3px;">${returnRate.toFixed(
                  1,
                )}%</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Return Rate</div>
              </div>
              `
                  : `
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #f39c12; margin-bottom: 3px;">PKR ${avgPrice.toFixed(
                  2,
                )}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Avg Price</div>
              </div>
              `
              }
            </div>
            
            ${
              itemsReturned > 0
                ? `
            <!-- Net Metrics -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 16px; padding: 10px; background: rgba(40, 167, 69, 0.1); border-radius: 6px; border: 1px solid rgba(40, 167, 69, 0.2);">
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #28a745; margin-bottom: 3px;">PKR ${netRevenue.toLocaleString()}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Net Revenue</div>
              </div>
              <div style="text-align: center;">
                <div style="font-size: 16px; font-weight: 800; color: #28a745; margin-bottom: 3px;">${netItems.toLocaleString()}</div>
                <div style="font-size: 10px; color: #6c757d; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Net Items</div>
              </div>
            </div>
            `
                : ''
            }

            ${
              variations.length > 0
                ? `
            <div style="margin-top: 8px;">
              <div style="display: flex; flex-direction: column; gap: 2px;">
                ${variations
                  .map((variation) => {
                    const variationPercentage = (
                      (variation.items_sold / itemsSold) *
                      100
                    ).toFixed(1)
                    const variationTotalItems =
                      variation.items_sold + (variation.items_returned || 0)
                    const returnPercentage =
                      variationTotalItems > 0
                        ? ((variation.items_returned || 0) /
                            variationTotalItems) *
                          100
                        : 0
                    const barWidth = (variation.items_sold / itemsSold) * 100
                    const returnBarWidth =
                      variationTotalItems > 0
                        ? ((variation.items_returned || 0) /
                            variationTotalItems) *
                          100
                        : 0
                    const netRevenue =
                      variation.revenue - (variation.revenue_returned || 0)
                    const netItems =
                      variation.items_sold - (variation.items_returned || 0)
                    const hasReturns = (variation.items_returned || 0) > 0

                    return `
                    <div style="position: relative; padding: 4px 0; margin-bottom: 8px; background: rgba(255,255,255,0.5); border-radius: 4px; padding: 8px;">
                      <!-- SKU and main metrics -->
                      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 10px; margin-bottom: 4px;">
                        <span style="color: #6c757d; font-family: monospace; font-weight: 600;">SKU: ${
                          variation.sku
                        }</span>
                        <span style="color: #5f38f9; font-weight: 600;">PKR ${variation.revenue.toLocaleString()}</span>
                        <span style="color: #495057; font-weight: 500;">${
                          variation.items_sold
                        } (${variationPercentage}%)</span>
                      </div>
                      
                      ${
                        hasReturns
                          ? `
                      <!-- Returns info -->
                      <div style="display: flex; justify-content: space-between; align-items: center; font-size: 9px; margin-bottom: 4px; color: #dc3545;">
                        <span>Returns: ${
                          variation.items_returned
                        } items (${returnPercentage.toFixed(1)}%)</span>
                        <span>PKR ${(
                          variation.revenue_returned || 0
                        ).toLocaleString()}</span>
                        <span style="color: #28a745;">Net: ${netItems} items, PKR ${netRevenue.toLocaleString()}</span>
                      </div>
                      `
                          : ''
                      }
                      
                      <!-- Progress bar -->
                      <div style="position: relative; width: 100%; height: 4px; background: rgba(0,0,0,0.08); border-radius: 2px; overflow: hidden;">
                        <!-- Sold items bar -->
                        <div style="width: ${barWidth}%; height: 100%; background: linear-gradient(90deg, #5f38f5 0%, #7c3aed 100%); border-radius: 2px; position: absolute; top: 0; left: 0;"></div>
                        ${
                          hasReturns
                            ? `
                        <!-- Returns overlay bar -->
                        <div style="width: ${returnBarWidth}%; height: 100%; background: linear-gradient(90deg, #dc3545 0%, #c82333 100%); border-radius: 2px; position: absolute; top: 0; left: 0; opacity: 0.7;"></div>
                        `
                            : ''
                        }
                      </div>
                    </div>
                  `
                  })
                  .join('')}
              </div>
            </div>
          `
                : ''
            }
          </div>
        </div>
      `
    },
  },
  xAxis: {
    show: false, // Hide x-axis labels
  },
  yAxis: {
    axisLabel: {
      show: false, // Hide Y-axis labels but keep the data structure
    },
  },
})

// Shared chart data creation
export const createChartData = (
  sortedData: TopProduct[],
  sortBy: 'revenue' | 'items_sold',
  maxItems: number = 10,
) => {
  if (!sortedData.length) {
    return {
      yAxis: [],
      series: [{ name: 'Revenue', type: 'bar', data: [] }],
    }
  }

  const topProducts = [...sortedData].slice(0, maxItems)

  return {
    yAxis: topProducts.map((product) => product.product_name).reverse(),
    series: [
      {
        name: sortBy === 'revenue' ? 'Revenue' : 'Items Sold',
        type: 'bar',
        data: topProducts
          .map((product) =>
            sortBy === 'revenue'
              ? product.total_revenue || 0
              : product.total_items_sold || 0,
          )
          .reverse(),
        itemStyle: { color: colors?.primary || '#5f38f9' },

        barWidth: '80%',
      },
    ],
  }
}

// Shared data sorting
export const sortProductsData = (
  data: TopProduct[],
  sortBy: 'revenue' | 'items_sold',
) => {
  if (!data) return []

  return [...data].sort((a, b) => {
    if (sortBy === 'revenue') {
      return (b.total_revenue || 0) - (a.total_revenue || 0)
    } else {
      return (b.total_items_sold || 0) - (a.total_items_sold || 0)
    }
  })
}

// Shared CSV download functionality
export const downloadProductsCSV = (
  sortedData: TopProduct[],
  sortBy: 'revenue' | 'items_sold',
) => {
  if (!sortedData.length) return

  const headers = [
    'Product Name',
    'SKU',
    'Total Revenue (PKR)',
    'Total Items Sold',
    'Total Items Returned',
    'Total Revenue Returned (PKR)',
    'Net Revenue (PKR)',
    'Net Items',
    'Return Rate (%)',
    'Average Price (PKR)',
    'Collections',
    'Variations Count',
  ]

  const csvContent = [
    headers.join(','),
    ...sortedData.map((product) => {
      const avgPrice =
        product.total_items_sold > 0
          ? (product.total_revenue / product.total_items_sold).toFixed(2)
          : '0.00'
      const totalItems =
        product.total_items_sold + (product.items_returned || 0)
      const returnRate =
        totalItems > 0
          ? (((product.items_returned || 0) / totalItems) * 100).toFixed(2)
          : '0.00'
      const netRevenue = product.total_revenue - (product.revenue_returned || 0)
      const netItems = product.total_items_sold - (product.items_returned || 0)

      return [
        `"${product.product_name}"`,
        product.variations?.[0]?.sku || 'N/A',
        product.total_revenue.toLocaleString(),
        product.total_items_sold.toLocaleString(),
        (product.items_returned || 0).toLocaleString(),
        (product.revenue_returned || 0).toLocaleString(),
        netRevenue.toLocaleString(),
        netItems.toLocaleString(),
        returnRate,
        avgPrice,
        `"${product.collection_names?.join('; ') || 'N/A'}"`,
        product.variations?.length || 0,
      ].join(',')
    }),
  ].join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute(
    'download',
    `top-products-${sortBy}-${new Date().toISOString().split('T')[0]}.csv`,
  )
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
